import "./App.css";

import Dashboard from "./pages/Dashboard";
import ProductsPage from "./pages/Products";
import Settings from "./pages/Settings";
import { LoginPage } from "./pages/Login";
import { AdminImpersonationPage } from "./pages/AdminImpersonation";
import {
    BrowserRouter,
    Routes,
    Route,
    Navigate,
    useLocation,
} from "react-router-dom";

import { AppSidebar } from "@/components/sidebar/AppSidebar";
import { Banner } from "@/components/ui/banner";

import {
    SidebarProvider,
    SidebarInset,
    SidebarTrigger,
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbList,
    BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { ThemeProvider } from "@/components/ui/theme-provider";

import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedPage from "./pages/ProtectedPage";
import IntegrationsPage from "./pages/Integrations";
import { Toaster } from "./components/ui/toaster";
import Support from "./pages/Support";
import DiscountsPage from "./pages/Discounts";
import { useImpersonationStore } from "@/stores/impersonationStore";
import PricingModelsPage from "./pages/PricingModels";
import PricingModelDetail from "./pages/PricingModelDetail";

function AppContent() {
    const location = useLocation();
    const isDetailPage =
        location.pathname.startsWith("/pricing-models/") &&
        location.pathname !== "/pricing-models";

    // Get page title for breadcrumb
    const getPageTitle = (pathname: string) => {
        const routes: Record<string, string> = {
            "/dashboard": "Dashboard",
            "/pricing-models": "Pricing Models",
            "/products": "Products",
            "/settings": "Settings",
            "/integrations": "Integrations",
            "/discounts": "Discounts",
            "/support": "Support",
        };
        return routes[pathname] || "Page";
    };

    return (
        <SidebarProvider
            defaultOpen={
                document.cookie
                    .split("; ")
                    .find((row) => row.startsWith("sidebar:state="))
                    ?.split("=")[1] === "true"
            }
            style={
                {
                    "--banner-height": useImpersonationStore.getState()
                        .impersonationEmail
                        ? "3rem"
                        : "0px",
                } as React.CSSProperties
            }
        >
            {useImpersonationStore.getState().impersonationEmail && (
                <Banner>
                    <span className="text-sm font-medium">
                        <b>CUSTOMER VIEW MODE</b>: You are currently viewing as{" "}
                        <b>
                            {
                                useImpersonationStore.getState()
                                    .impersonationEmail
                            }
                        </b>
                    </span>
                </Banner>
            )}
            <AppSidebar />
            <SidebarInset>
                {!isDetailPage && (
                    <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 border-b">
                        <div className="flex items-center gap-2 px-4">
                            <SidebarTrigger className="-ml-1" />
                            <Separator
                                orientation="vertical"
                                className="mr-2 data-[orientation=vertical]:h-4"
                            />
                            <Breadcrumb>
                                <BreadcrumbList>
                                    <BreadcrumbItem>
                                        <BreadcrumbPage>
                                            {getPageTitle(location.pathname)}
                                        </BreadcrumbPage>
                                    </BreadcrumbItem>
                                </BreadcrumbList>
                            </Breadcrumb>
                        </div>
                    </header>
                )}
                {isDetailPage ? (
                    <ProtectedPage />
                ) : (
                    <div className="flex flex-1 flex-col gap-4 p-4">
                        <ProtectedPage />
                    </div>
                )}
                <Toaster />
            </SidebarInset>
        </SidebarProvider>
    );
}

function App() {
    return (
        <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
            <AuthProvider>
                <BrowserRouter>
                    <Routes>
                        <Route path="/login" element={<LoginPage />} />
                        <Route
                            path="/admin/impersonate"
                            element={<AdminImpersonationPage />}
                        />
                        <Route element={<AppContent />}>
                            <Route
                                index
                                element={<Navigate to="/dashboard" replace />}
                            />
                            <Route path="/dashboard" element={<Dashboard />} />
                            <Route
                                path="/products"
                                element={<ProductsPage />}
                            />
                            <Route
                                path="/pricing-models"
                                element={<PricingModelsPage />}
                            />
                            <Route
                                path="/pricing-models/:id"
                                element={<PricingModelDetail />}
                            />
                            <Route
                                path="/discounts"
                                element={<DiscountsPage />}
                            />
                            <Route
                                path="/integrations"
                                element={<IntegrationsPage />}
                            />
                            <Route path="/settings" element={<Settings />} />
                            <Route path="/error" element={<div>Error</div>} />
                            <Route
                                path="*"
                                element={<Navigate to="/error" replace />}
                            />
                            <Route
                                path="/integrations/shopify/callback"
                                element={
                                    <Navigate to="/integrations" replace />
                                }
                            />
                            <Route path="/support" element={<Support />} />
                        </Route>
                    </Routes>
                </BrowserRouter>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;
